import { html, css, LitElement } from 'lit-element';
import '@trumedianetworks/tmn-core-components/src/tmn-checkbox';
import '@trumedianetworks/tmn-core-components/src/tmn-tabs';
import '@trumedianetworks/tmn-core-components/src/tmn-select';
import { connect } from '../store/connect';
import { selectors } from '../site/';
import { SOCCER_AUTH_DEFS } from './constants';

// default to only selected and select the first two, see if I know "active season" from dp
const LIMIT_TYPE = {
    NONE: 'NONE', // meaning all available
    ALLOW: 'ALLOW',
};

class SoccerSeasonAuthDetail extends LitElement {
    constructor() {
        super();
        this.seasons = [];
        this.seasonAuths = {};
        this.siteLevelAuths = {};
    }

    static get styles() {
        return [
            css`
                :host {
                    padding: 12px;
                    border-radius: 8px;
                    background-color: #f9f9f9;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    border: 1px solid var(--app-secondary-color);
                }

                :host([hidden]) {
                    display: none;
                }

                :host([isViewMode]) {
                    padding: unset;
                    /* background-color: unset; */
                    position: relative;
                    border: unset;
                }

                tmn-checkbox {
                    width: fit-content;
                }
                .detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 4px 0;
                }
                .detail-header > div {
                    display: flex;
                }
                .season-display {
                    border: 1px solid var(--app-secondary-color);
                    margin: 4px;
                    padding: 4px;
                    border-radius: 4px;
                    display: inline-flex;
                }
                .all-seasons-display {
                    margin: 4px;
                    padding: 4px;
                    display: inline-flex;
                    font-weight: 600;
                }
                .content {
                    padding: 12px;
                }
                :host([isViewMode]) .content {
                    padding: 6px;
                    padding-left: 12px;
                }

                tmn-tab-panel {
                    background-color: var(--app-surface-color);
                    border: 1px solid rgb(221, 221, 221);
                    border-top: 0;
                    border-radius: 8px;
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                }
                .limit-select {
                    max-width: 200px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            seasonAuths: {
                type: Object,
            },
            seasons: {
                type: Array,
            },
            isViewMode: {
                type: Boolean,
                reflect: true,
            },
            leagueId: {
                type: String,
            },
            siteLevelAuths: {
                type: Object,
            },
            hidden: {
                type: Boolean,
                reflect: true,
            },
        };
    }

    updated(changedProps) {
        if (changedProps.has('siteLevelAuths') || changedProps.has('seasonAuths')) {
            const { siteLevelAuths, seasonAuths } = this;
            this.hidden = !SOCCER_AUTH_DEFS.some(
                ({ siteLevelAuth, auth }) => siteLevelAuths[siteLevelAuth] && seasonAuths[auth]
            );
        }
        if (changedProps.has('hidden')) {
            // only need to do this on unhide to force-show the first panel
            if (!this.hidden) {
                const firstPanel = this.shadowRoot.querySelector('tmn-tab-panel');
                if (firstPanel) {
                    this.shadowRoot.querySelector('tmn-tab-group').selectedValue = firstPanel.value;
                }
            }
        }
    }

    render() {
        if (this.isViewMode) {
            return html`${this.renderDisplaySelectedSeasons()}`;
        }
        return html`${this.renderSeasonAuths()}`;
    }

    renderHeader() {
        return html` <div>Season authorizations</div> `;
    }

    renderSeasonAuths() {
        const { siteLevelAuths, seasonAuths } = this;

        const tabs = SOCCER_AUTH_DEFS.filter(
            ({ siteLevelAuth, auth }) => siteLevelAuths[siteLevelAuth] && seasonAuths[auth]
        );
        return html`
            <tmn-tab-group>
                <tmn-tabs
                    >${tabs.map(
                        ({ label, auth }) => html`<tmn-tab .value=${auth}>${label}</tmn-tab>`
                    )}</tmn-tabs
                >
                ${tabs.map(
                    ({ auth }) =>
                        html`<tmn-tab-panel .value=${auth}
                            ><div class="content">
                                ${this.renderEditSelectedSeasons(auth)}
                            </div></tmn-tab-panel
                        >`
                )}
            </tmn-tab-group>
        `;
    }

    renderDisplaySelectedSeasons() {
        const { seasons = [], siteLevelAuths = {}, seasonAuths = {} } = this;

        const allowedfilteredDefs = SOCCER_AUTH_DEFS.filter(
            ({ siteLevelAuth, auth }) => siteLevelAuths[siteLevelAuth] && seasonAuths[auth]
        );
        const authRenderData = [];
        allowedfilteredDefs.forEach(({ auth, label }) => {
            const { authSeasons, limitType } = this.getAuthDetails(auth);
            if (limitType === LIMIT_TYPE.NONE) {
                authRenderData.push(
                    html`<div>
                        ${label}:<span class="all-seasons-display">All seasons allowed</span>
                    </div>`
                );
            } else {
                const seasonNames = seasons
                    .filter(({ name }) => authSeasons.includes(name))
                    .map(({ displayKey }) => displayKey);

                if (seasonNames.length) {
                    authRenderData.push(
                        html`<div>
                            ${label}:${seasonNames.map(
                                (s) => html`<span class="season-display">${s}</span>`
                            )}
                        </div>`
                    );
                }
            }
            //}
        });
        if (authRenderData.length) {
            return html`<div class="content">${authRenderData}</div>`;
        }
    }

    renderEditSelectedSeasons(auth) {
        const { seasons = [] } = this;
        const { authSeasons = [], limitType } = this.getAuthDetails(auth);
        if (limitType === LIMIT_TYPE.ALLOW) {
            return seasons.map(({ displayKey, name }) => {
                const seasonSelected = authSeasons.includes(name);
                return this.renderCheckbox(auth, displayKey, name, seasonSelected);
            });
        }
        if (limitType === LIMIT_TYPE.NONE) {
            return html`<div class="limit-note">All authorized seasons allowed</div>`;
        }
    }

    renderCheckbox(auth, displayLabel, seasonId, checked = false) {
        const value = { auth, seasonId };
        return html`<tmn-checkbox
            label=${displayLabel}
            .value=${value}
            .checked=${checked}
            @tmn-switch-change=${this.handleCheckboxChanged}
            >${displayLabel}
        </tmn-checkbox> `;
    }

    handleCheckboxChanged(e) {
        const { leagueId } = this;
        const { checked, value } = e.detail;
        const { seasonId, auth } = value;
        const { authSeasons = [], limitType } = this.getAuthDetails(auth);
        const newSeasons = checked
            ? [...authSeasons, seasonId]
            : authSeasons.filter((s) => s !== seasonId);
        const newOptions = {
            leagueId,
            [auth]: {
                authSeasons: newSeasons,
                limitType,
            },
        };
        this.dispatchChanges(newOptions);
    }

    dispatchChanges(newOptions) {
        const event = new CustomEvent('soccer-season-auth-changed', {
            detail: {
                ...newOptions,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    getAuthDetails(auth) {
        const { seasonAuths = {} } = this;
        const { authSeasons = [], limitType = LIMIT_TYPE.NONE } = seasonAuths[auth] ?? {};
        return { authSeasons, limitType };
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            siteLevelAuths: selectors.activeRecordSiteAuths(state),
        };
    };
};

window.customElements.define(
    'soccer-season-auth-detail',
    connect(mapStateToProps)(SoccerSeasonAuthDetail)
);
